<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <BaseAlert
      v-else-if="!isPending && isHallFull"
      v-bind="noSeats"
      slot="middle"
    />
    <Event
      v-else
      :item="event"
      slot="middle"
      @show:rules="showRulesModal"
    >
      <template #actions>
        <BaseButton
          :text="chooseButtonText"
          is-outlined
          @click="showTicketsModal"
        />
        <BaseButton
          v-if="hasTickets"
          :href="$router.options.paths.cart"
          :text="$t('actions.placeOrder')"
        />
      </template>
    </Event>
    <template #root>
      <ModalTickets
        v-if="isTicketsModalVisible"
        :tickets="tickets"
        :available-seats="availableSeats"
        :scheme="scheme"
        :event-id="eventId"
        class="screen__overlay"
        @close="hideTicketsModal"
        @pick="setTickets"
      />
      <ModalRules
        v-if="isRulesModalVisible"
        class="screen__overlay"
        @close="hideRulesModal"
      />
    </template>
  </BaseScreen>
</template>

<script>
import {
  mapState, mapGetters, mapMutations, mapActions,
} from 'vuex';

import screenMixin from '@/mixins/screen';
import BaseButton from '@/components/BaseButton.vue';
import ModalRules from '@/components/ModalRules.vue';
import ModalTickets from '@/components/ModalTickets.vue';
import Event from '@/components/Event.vue';

import CONSTANTS from '@/helpers/constants';
import convertNameToLingvoStep from '@/helpers/convertNameToLingvoStep';

const {
  ACTION_OPEN_MODAL_TICKETS,
  ACTION_OPEN_MODAL_RULES,
  STEP_CONFIRM_CHOOSE_SEAT,
} = CONSTANTS;

export default {
  name: 'ChooseSeatScreen',
  mixins: [screenMixin],
  components: {
    BaseButton,
    ModalRules,
    ModalTickets,
    Event,
  },
  data() {
    return {
      isTicketsModalVisible: false,
      isRulesModalVisible: false,
      availableSeats: [],
      scheme: null,
    };
  },
  computed: {
    ...mapState('cart', [
      'event',
      'tickets',
    ]),
    ...mapGetters('cart', [
      'sum',
      'ticketsCount',
    ]),
    title() {
      return this.$t('titles.chooseSeat');
    },
    pendingText() {
      return this.$t('pending.fetch');
    },
    error() {
      return {
        title: this.$t('titles.badConnection'),
        text: this.$t('texts.buyNotPossible'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
        ],
      };
    },
    noSeats() {
      return {
        title: this.$t('titles.fullHall'),
        text: this.$t('texts.noSeats'),
        actions: [
          {
            text: this.$t('actions.anotherPick'),
            action: () => this.$router.push(this.$router.options.paths.events),
          },
        ],
      };
    },
    hasTickets() {
      return this.ticketsCount > 0;
    },
    ticketsCountText() {
      return this.$tc('pluralization.ticket', this.ticketsCount);
    },
    chooseButtonText() {
      if (!this.hasTickets) {
        return this.$t('actions.chooseSeat');
      }
      return `${this.ticketsCountText},${this.$options.filters.sum(this.sum, true)}`;
    },
    isHallFull() {
      return this.availableSeats.length === 0;
    },
    schemeId() {
      return this.event.schemeId;
    },
    eventId() {
      return this.event.id;
    },
  },
  created() {
    this.initActions();
    this.checkAvailableSeats();
  },
  methods: {
    ...mapMutations('cart', [
      'setTickets',
    ]),
    ...mapActions('cart', [
      'fetchSchemeById',
      'fetchAvailableSeatsByEventId',
    ]),
    async checkAvailableSeats() {
      try {
        this.showPending(0);
        const [availableSeats, scheme] = await Promise.all([
          this.fetchAvailableSeatsByEventId(this.eventId),
          this.fetchSchemeById(this.schemeId),
        ]);
        this.availableSeats = availableSeats;
        this.scheme = scheme;
        this.hidePending();
      } catch {
        this.showError();
      }
    },
    showTicketsModal() {
      this.isTicketsModalVisible = true;
    },
    hideTicketsModal() {
      this.isTicketsModalVisible = false;
      this.sayConfirmReplic();
    },
    showRulesModal() {
      this.isRulesModalVisible = true;
    },
    hideRulesModal() {
      this.isRulesModalVisible = false;
    },
    sayConfirmReplic() {
      if (this.hasTickets) {
        this.$robot.dialogService.setCase(STEP_CONFIRM_CHOOSE_SEAT, true, true);
        this.$robot.dialogService.sayReplicByName(STEP_CONFIRM_CHOOSE_SEAT, true);
      } else {
        const name = convertNameToLingvoStep(this.$route.name, 'screen');
        this.$robot.dialogService.setCase(name, true, true);
        this.$robot.dialogService.sayReplicByName(name, true);
      }
    },
    initActions() {
      const rootActions = [
        [ACTION_OPEN_MODAL_TICKETS, this.showTicketsModal],
        [ACTION_OPEN_MODAL_RULES, this.showRulesModal],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        rootActions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
</script>
